import { useEffect, useMemo } from 'react';
import { Control, useWatch } from 'react-hook-form';
import { useHistory, useLocation } from 'react-router';

import { Range } from '@dreryk/watermelon';

import { visitPaths } from '~libs.desktop.shared.paths';

import { DateFilter, DateFilterType } from '../../../AppointmentsFilterChips/DateChipFilter.types';
import {
   DATE_RANGE_SEPARATOR,
   formatDateToUrl,
   getEndOfDay,
   getNextDay,
   getStartOfDay,
   getToday,
} from '../../../AppointmentsFilterChips/DateChipFilter.utils';
import { FilterFormBean } from '../../AppointmentsFilterPanel';

import { DateRangeField } from './DateRangeField';

const REGEX_COMMA_ESCAPE = /%2C/u;

const useGetFilterRangeValue = (control: Control<FilterFormBean>, defaultRange: Range<Date>): Range<Date> => {
   return useWatch<Range<Date>>({
      control: control,
      name: DateRangeField.fieldName,
      defaultValue: defaultRange,
   });
};

export const useUpdateUrlFromDateRange = (control: Control<FilterFormBean>, defaultRange: Range<Date>): void => {
   const history = useHistory();
   const location = useLocation();

   const range = useGetFilterRangeValue(control, defaultRange);

   useEffect(() => {
      if (range) {
         if (range.hasUpperBound() && range.hasLowerBound()) {
            const start = formatDateToUrl(new Date(range.lowerEndpoint()));
            const end = formatDateToUrl(new Date(range.upperEndpoint()));

            const searchParams = new URLSearchParams(location.search);
            searchParams.set(visitPaths.APPOINTMENTS_DATE_SEARCH_PARAM, `${start}${DATE_RANGE_SEPARATOR}${end}`);

            history.replace({
               search: searchParams.toString().replace(REGEX_COMMA_ESCAPE, DATE_RANGE_SEPARATOR),
               state: location.state,
            });
         }
      }
      // eslint-disable-next-line
   }, [range]);
};

export const useRangeValue = (
   previousFilterType: DateFilter['type'] | null | undefined,
   dateFilterValue: DateFilter['range'] | undefined
): Range<Date> => {
   return useMemo(() => {
      if (!dateFilterValue) {
         const today = getToday();
         return Range.closed(getStartOfDay(today), getNextDay(getEndOfDay(today)));
      }

      if (previousFilterType && previousFilterType !== DateFilterType.RANGE) {
         const startDate = dateFilterValue.lowerEndpoint();
         return Range.closed(getStartOfDay(startDate), getNextDay(getEndOfDay(startDate)));
      }

      return dateFilterValue;
      // eslint-disable-next-line react-hooks/exhaustive-deps
   }, [dateFilterValue]);
};
